import { Injectable, signal } from '@angular/core';
import { ColorShadeUtility } from '../pages/company-settings/color-shade.utility';

@Injectable({ providedIn: 'root' })
export class CrmBrandingStateService {
  private readonly brandingColoursSessionKey = 'brandingcolour';

  /** Product defaults — kept in sync with the `:root` values in styles.css. */
  private readonly defaultPrimaryColor = '#4F46E5';
  private readonly defaultSecondaryColor = '#EA580C';

  readonly logoUrl = signal<string | null>(null);
  readonly secondaryColor = signal<string>('#EA580C');
  readonly primaryColor = signal<string>('#4F46E5');

  /** Apply branding colors from sessionStorage (if available) without calling the API. */
  hydrateFromSession(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const raw = sessionStorage.getItem(this.brandingColoursSessionKey);
    if (!raw) {
      return;
    }

    try {
      const parsed = JSON.parse(raw) as {
        primaryColor?: string;
        secondaryColor?: string;
      };

      const primary = parsed.primaryColor?.trim();
      const secondary = parsed.secondaryColor?.trim();

      if (primary) {
        const cleanPrimary = primary.replace('#', '').toLowerCase();
        const primaryShades = ColorShadeUtility.generateShades(cleanPrimary);
        this.applyCSSVariables('primary', primaryShades);
        this.primaryColor.set(`#${cleanPrimary}`);
      }

      if (secondary) {
        const cleanSecondary = secondary.replace('#', '').toLowerCase();
        const secondaryShades = ColorShadeUtility.generateShades(cleanSecondary);
        this.applyCSSVariables('secondary', secondaryShades);
        this.secondaryColor.set(`#${cleanSecondary}`);
      }
    } catch {
      // Ignore malformed sessionStorage
    }
  }

  setLogoUrl(logoUrl: string | null | undefined): void {
    const normalizedLogoUrl = logoUrl?.trim();
    this.logoUrl.set(normalizedLogoUrl || null);
  }

  setLogoUrlForFreshUpload(logoUrl: string | null | undefined): void {
    this.setLogoUrl(this.withCacheBust(logoUrl));
  }

  setColors(primaryColor: string | null | undefined, secondaryColor: string | null | undefined): void {
    if (primaryColor) {
      const cleanColor = primaryColor.replace('#', '').toLowerCase();
      const primaryShades = ColorShadeUtility.generateShades(cleanColor);
      this.applyCSSVariables('primary', primaryShades);
      const nextPrimary = `#${cleanColor}`;
      this.primaryColor.set(nextPrimary);
    }

    if (secondaryColor) {
      const cleanColor = secondaryColor.replace('#', '').toLowerCase();
      const secondaryShades = ColorShadeUtility.generateShades(cleanColor);
      this.applyCSSVariables('secondary', secondaryShades);
      const nextSecondary = `#${cleanColor}`;
      this.secondaryColor.set(nextSecondary);
    }

    // Keep both values synced in one session key.
    this.persistBrandingColorsToSession();
  }

  private persistBrandingColorsToSession(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const payload = {
      primaryColor: this.primaryColor(),
      secondaryColor: this.secondaryColor(),
    };

    sessionStorage.setItem(this.brandingColoursSessionKey, JSON.stringify(payload));
  }

  private applyCSSVariables(prefix: 'primary' | 'secondary', shades: Record<number, string>): void {
    const root = document.documentElement;
    const shadeKeys = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

    shadeKeys.forEach((shade) => {
      const varName = `--${prefix}-${shade}`;
      const colorValue = shades[shade];
      if (colorValue) {
        root.style.setProperty(varName, colorValue);

        // Also set RGB variant for use in rgba() functions
        const rgbValue = this.hexToRgb(colorValue);
        if (rgbValue) {
          root.style.setProperty(`${varName}-rgb`, rgbValue);
        }
      }
    });
  }

  private hexToRgb(hex: string): string | null {
    const cleanHex = hex.replace('#', '');
    const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(cleanHex);
    if (!result) return null;
    return `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`;
  }

  withCacheBust(logoUrl: string | null | undefined): string {
    const normalizedLogoUrl = logoUrl?.trim();
    if (!normalizedLogoUrl) {
      return '';
    }

    const separator = normalizedLogoUrl.includes('?') ? '&' : '?';
    return `${normalizedLogoUrl}${separator}v=${Date.now()}`;
  }

  /** Used for logout/cleanup */
  clearSessionBrandingColors(): void {
    if (typeof window === 'undefined') {
      return;
    }
    sessionStorage.removeItem(this.brandingColoursSessionKey);
  }

  /**
   * Fully reset branding back to product defaults.
   *
   * `clearSessionBrandingColors()` only drops the persisted value — it does NOT
   * revert the CSS variables already written onto `document.documentElement`,
   * so the tenant's colors keep bleeding into the login screen after logout/401.
   * This re-applies the default palette to the DOM, resets the signals and the
   * logo, and clears the persisted session value in one step.
   */
  resetToDefaultBranding(): void {
    const cleanPrimary = this.defaultPrimaryColor.replace('#', '').toLowerCase();
    const cleanSecondary = this.defaultSecondaryColor.replace('#', '').toLowerCase();

    this.applyCSSVariables('primary', ColorShadeUtility.generateShades(cleanPrimary));
    this.applyCSSVariables('secondary', ColorShadeUtility.generateShades(cleanSecondary));

    this.primaryColor.set(this.defaultPrimaryColor);
    this.secondaryColor.set(this.defaultSecondaryColor);
    this.logoUrl.set(null);

    this.clearSessionBrandingColors();
  }
}
